import { motion } from "framer-motion";
import { friendlyRange } from "./compare/headlineTotals";
import { Sparkles } from "lucide-react";
import { PayoffInventory } from "./PayoffInventory";
import { PayoffAddOn } from "./PayoffAddOn";
import { buildPayoffStory } from "./payoffStory";

const fmt = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return Math.round(n).toLocaleString();
};

const rangeText = (s, e) => friendlyRange(s, e) || "this period";

export const ROIStoryHero = ({
  clientName = "Your Business",
  reportStart,
  reportEnd,
  mobileSummary,
  metaSummary,
  socialMirrorSummary,
  facebookCampaign,
  ctvPublishers = [],
  googleAdsData,
  tiktokImpressions = 0,
  tiktokClicks = 0,
  tiktokReach = 0,
  pmaxImpressions = 0,
  pmaxClicks = 0,
  appfrontData,
  brandChannelImpressions = 0,
  trafficData,
  previousPeriod = null,
  payoffNote,
  onSavePayoffNote = () => {},
  readOnly = false,
}) => {
  const mobileImpressions = mobileSummary?.overview?.total_impressions || 0;
  const mobileClicks = mobileSummary?.overview?.total_clicks || 0;
  const fbImpressions = facebookCampaign?.has_data ? facebookCampaign?.overview?.impressions || 0 : metaSummary?.overview?.total_impressions || 0;
  const fbClicks = facebookCampaign?.has_data ? facebookCampaign?.overview?.clicks || 0 : metaSummary?.overview?.total_clicks || 0;
  const fbReach = facebookCampaign?.overview?.reach || metaSummary?.overview?.reach || 0;
  const smImpressions = socialMirrorSummary?.overview?.total_impressions || 0;
  const smClicks = socialMirrorSummary?.overview?.total_clicks || 0;
  const ctvImpressions = ctvPublishers.reduce((s, p) => s + (p.impressions || 0), 0);
  const gaImpressions = googleAdsData?.summary?.total_impressions || 0;
  const gaClicks = googleAdsData?.summary?.total_clicks || 0;
  const afImpressions = appfrontData?.summary?.total_impressions || 0;

  const channelImpressions = [
    ["Mobile ads", mobileImpressions], ["Facebook & Instagram", fbImpressions], ["Social Mirror", smImpressions],
    ["Streaming TV", ctvImpressions], ["Google Search", gaImpressions], ["TikTok", tiktokImpressions],
    ["App Front", afImpressions], ["Google Performance Max", pmaxImpressions], ["TV, radio & other", brandChannelImpressions],
  ];
  const totals = {
    impressions: channelImpressions.reduce((s, [, v]) => s + v, 0),
    channels: channelImpressions.filter(([, v]) => v > 0).length,
    clicks: mobileClicks + fbClicks + smClicks + gaClicks + tiktokClicks + pmaxClicks,
    visits: trafficData?.totals?.total_sessions || 0,
    direct: trafficData?.totals?.direct_sessions || 0,
    organic: trafficData?.totals?.organic_sessions || 0,
    reach: fbReach,
  };

  if (totals.impressions === 0 && totals.visits === 0) return null;

  const story = buildPayoffStory({
    clientName, rangeText: rangeText(reportStart, reportEnd), t: totals, prev: previousPeriod?.totals, fmt,
  });

  const inventory = [
    ["Times your brand was seen", fmt(totals.impressions)],
    ...channelImpressions.filter(([, v]) => v > 0).map(([n, v]) => [`Seen on ${n}`, fmt(v)]),
    fbReach > 0 && ["People reached on Facebook & Instagram", fmt(fbReach)],
    tiktokReach > 0 && ["People reached on TikTok", fmt(tiktokReach)],
    totals.clicks > 0 && ["Ad clicks", fmt(totals.clicks)],
    totals.visits > 0 && ["Website visits", fmt(totals.visits)],
    totals.direct > 0 && ["Visits from people typing your name", fmt(totals.direct)],
    totals.organic > 0 && ["Visits from Google search", fmt(totals.organic)],
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative z-10 mb-10"
      data-testid="roi-story-hero"
    >
      <div className="relative overflow-hidden rounded-3xl border border-lime-300/20 bg-[#0a3d54]">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-lime-300/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />

        <div className="relative p-6 md:p-12 max-w-4xl">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-lime-300" />
            <p className="text-lime-300 text-[11px] font-bold uppercase tracking-[0.25em]">Is it working?</p>
          </div>

          <p className="text-white text-lg md:text-2xl leading-relaxed md:leading-[1.6]" data-testid="payoff-story">
            {story.map((sentence, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 + i * 0.12, duration: 0.5 }}
              >
                {sentence}
              </motion.span>
            ))}
          </p>

          <PayoffAddOn note={payoffNote} readOnly={readOnly} onSave={onSavePayoffNote} />

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col gap-4">
            <p className="text-slate-400 text-xs md:text-sm italic" data-testid="cant-measure-note">
              Some of the best results never show up here: word of mouth, and customers who saw an ad and came in months later.
            </p>
            <PayoffInventory rows={inventory} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
